import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { binService } from "../../services/binService";
import { useAuth } from "../../contexts/AuthContext";
import { useBin } from "../../query/hooks";
import { emptyBin, queryKeys } from "../../query/bin";
import { anonymousBinService } from "../../utils/anonymousBinService";
import {
  TrashIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";
import toast from "react-hot-toast";
import WhatsAppButton from "../Common/WhatsAppButton";

const Bin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const binQuery = useBin(!!user);
  const [anonBin, setAnonBin] = useState(() => anonymousBinService.getBin());
  const [assigning, setAssigning] = useState(false);
  const [updating, setUpdating] = useState({});
  const [message, setMessage] = useState("");

  const binData = user ? binQuery.data || emptyBin() : anonBin;
  const loading = user ? binQuery.isLoading : false;

  const refreshBin = async () => {
    if (!user) {
      setAnonBin(anonymousBinService.getBin());
      return;
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.bin });
  };

  useEffect(() => {
    if (binQuery.isError) setMessage("Error loading bin data");
  }, [binQuery.isError]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      await handleRemoveItem(itemId);
      return;
    }

    setUpdating((prev) => ({ ...prev, [itemId]: true }));
    setMessage("");

    try {
      if (!user) {
        const next = anonymousBinService.updateQuantity(itemId, newQuantity);
        setAnonBin(next);
      } else {
        await binService.updateBinItem(itemId, newQuantity);
        await refreshBin();
      }
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      setMessage("Error updating quantity");
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    setMessage("");

    try {
      if (!user) {
        const next = anonymousBinService.removeFromBin(itemId);
        setAnonBin(next);
      } else {
        await binService.removeFromBin(itemId);
        await refreshBin();
      }
      setMessage("Item removed from bin");
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error removing item:", error);
      setMessage("Error removing item from bin");
    }
  };

  const handleClearBin = async () => {
    if (!window.confirm("Are you sure you want to clear your entire bin?")) {
      return;
    }

    setMessage("");

    try {
      if (!user) {
        anonymousBinService.clearBin();
        setAnonBin(anonymousBinService.getBin());
      } else {
        await binService.clearBin();
        await refreshBin();
      }
      setMessage("Bin cleared successfully");
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error clearing bin:", error);
      setMessage("Error clearing bin");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  const binReadOnly = Boolean(user && binData.validationStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">My Bin</h1>
        <p className="text-sm sm:text-base text-gray-600">
          Review your selected materials before connecting with an agent.
        </p>
        {binReadOnly && (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This bin has been validated. You cannot change quantities or remove
            items here. To add materials, use the materials list or a material
            detail page; you will be asked to confirm because that will send the
            bin back for agent validation.
          </p>
        )}
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully") || message.includes("removed")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Empty Bin */}
      {!binData.materials?.length ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ShoppingCartIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Your bin is empty
          </h2>
          <p className="text-gray-600 mb-6">
            Start by browsing our materials and adding them to your bin.
          </p>
          <Link
            to="/materials"
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Browse Materials
          </Link>
        </div>
      ) : (
        <>
          {/* Bin Items */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Items ({binData?.materials?.length || 0})
              </h2>
            </div>

            <div className="divide-y divide-gray-200">
              {binData?.materials?.map((item) => {
                const linePrice =
                  item.price != null
                    ? item.price
                    : (item.material?.pricePerKg || 0) * item.quantity;
                const materialId = item.material?._id || item.material;

                return (
                  <div key={item._id} className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
                        <div className="flex-shrink-0">
                          {item.material?.image ? (
                            <img
                              src={item.material.image}
                              alt={item.material.name}
                              className="h-14 w-14 sm:h-16 sm:w-16 object-cover rounded-md"
                            />
                          ) : (
                            <div className="h-14 w-14 sm:h-16 sm:w-16 bg-gray-200 rounded-md flex items-center justify-center">
                              <span className="text-gray-500 text-xs">No Image</span>
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900 break-words">
                            {item.material?.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Category:{" "}
                            {item.material?.category
                              ?.replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </p>
                          <p className="text-sm text-gray-600">
                            Price: ₦
                            {item.material?.pricePerKg?.toFixed(2) || "0.00"}/kg
                          </p>
                          <p className="mt-1 text-base font-semibold text-gray-900 sm:hidden">
                            ₦{Number(linePrice || 0).toFixed(2)}
                          </p>
                        </div>

                        {!binReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(materialId)}
                            className="shrink-0 self-start rounded-md p-2 text-red-600 hover:bg-red-50 hover:text-red-800 sm:hidden"
                            title="Remove item"
                            aria-label={`Remove ${item.material?.name || "item"}`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-4">
                        <div className="flex items-center gap-2">
                          {binReadOnly ? (
                            <span className="min-w-[3.5rem] text-center text-base font-medium text-gray-900 sm:text-lg">
                              {item.quantity} kg
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  handleQuantityChange(item._id, item.quantity - 1)
                                }
                                disabled={updating[item._id]}
                                className="h-9 w-9 rounded bg-gray-200 text-lg font-semibold text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                                aria-label="Decrease quantity"
                              >
                                -
                              </button>
                              <span className="min-w-[3rem] text-center text-base font-medium text-gray-900 sm:text-lg">
                                {updating[item._id] ? "..." : `${item.quantity} kg`}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleQuantityChange(item._id, item.quantity + 1)
                                }
                                disabled={updating[item._id]}
                                className="h-9 w-9 rounded bg-gray-200 text-lg font-semibold text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                                aria-label="Increase quantity"
                              >
                                +
                              </button>
                            </>
                          )}
                        </div>

                        <div className="hidden text-right sm:block">
                          <p className="text-lg font-semibold text-gray-900">
                            ₦{Number(linePrice || 0).toFixed(2)}
                          </p>
                        </div>

                        {!binReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(materialId)}
                            className="hidden rounded-md p-2 text-red-600 hover:bg-red-50 hover:text-red-800 sm:inline-flex"
                            title="Remove item"
                            aria-label={`Remove ${item.material?.name || "item"}`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bin Summary */}
            <div className="bg-gray-50 px-4 sm:px-6 py-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Total: ₦
                    {binData?.totalPrice?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {binData?.materials?.length || 0} item(s)
                  </p>
                </div>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
                  {binData?.validationStatus && (
                    <button
                      onClick={() => navigate("/sell")}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-colors sm:w-auto"
                    >
                      Proceed to Sell
                    </button>
                  )}
                  {!binData?.validationStatus && (
                    <>
                      <button
                        type="button"
                        onClick={handleClearBin}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 px-4 rounded-md transition-colors sm:w-auto"
                      >
                        Clear Bin
                      </button>
                      <button
                        type="button"
                        disabled={assigning}
                        aria-busy={assigning}
                        onClick={async () => {
                          if (assigning) return;
                          if (!user) {
                            navigate("/login");
                            return;
                          }
                          if (binData?.selectedAgent) {
                            navigate("/sell");
                            return;
                          }
                          setAssigning(true);
                          try {
                            const result = await binService.autoAssign();
                            toast.success(
                              `${result?.data?.agentName || "An agent"} is assigned. Continue on the sell page.`
                            );
                            navigate("/sell");
                          } catch (error) {
                            toast.error(
                              error.response?.data?.message || "Could not assign an agent"
                            );
                          } finally {
                            setAssigning(false);
                          }
                        }}
                        className="inline-flex w-full min-w-0 items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:cursor-wait disabled:opacity-80 text-white font-medium py-2.5 px-4 rounded-md transition-colors sm:w-auto sm:min-w-[11rem]"
                      >
                        {assigning ? (
                          <>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                            Finding your agent...
                          </>
                        ) : (
                          "Proceed to sell"
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Next Steps
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-blue-800">
              <li>Review your selected materials and quantities above</li>
              <li>Proceed to sell. Jhonks assigns a nearby agent for you</li>
              <li>The agent validates the weight, then you are paid</li>
            </ol>
            <div className="mt-4">
              <WhatsAppButton />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Bin;
